import { Avatar, Box, Flex, Table } from 'components';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox } from 'components/_form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullseye,
  faSquarePen,
  faPeopleGroup,
  faLockOpen,
  faPeopleRoof,
  faPeopleLine,
  faFileLines
} from '@fortawesome/free-solid-svg-icons';
import { faFileLines as faRegluarFileLines } from '@fortawesome/free-regular-svg-icons';

import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { ColumnsOptions } from 'components/Table/components/ColumnsOptions';
import { theme } from 'styles';
import { getAssessment } from 'api/assessments';
import { IAssessment } from 'types/assessments';
import { useAssessmentContext } from 'contexts/AssessmentContext';
import { toast } from 'react-toastify';
import { EditAssessmentModal } from '../AssessmentActionsModal/EditAssessmentModal';
import { useNavigate } from 'react-router-dom';
import { parseItemsToMethodologyTree } from 'utilities/practices';
import { Loader } from 'components/Loader/Loader';
import { OptionButtons } from './components/OptionButtons';
import { format } from 'date-fns';
import { dateFormat } from 'constants/dateFormats/dateFormat';

const typeColorsDict = {
  normal: theme.palette.neutral.grey,
  target: theme.palette.accent.blue,
  baseline: theme.palette.accent.red,
  current: theme.palette.accent.green,
  derivative: theme.palette.accent.brown
};

interface Props {
  showAllButton?: boolean;
}

export const PracticeBasedAssessments: React.FC<Props> = ({
  showAllButton
}) => {
  const [hiddenItems, setHiddenItems] = useState<string[]>([]);
  const [loadingAssessmentIds, setLoadingAssessmentIds] = useState<number[]>(
    []
  );

  const {
    filters,
    assessments,
    setAssessments,
    selectedAssessments,
    setSelectedAssessments,
    setSelectedAssessment,
    handleLoadAssessments,
    assessmentToEditId,
    setAssessmentToEditId,
    setSelectedBlock
  } = useAssessmentContext();

  const { t } = useTranslation();

  const navigate = useNavigate();

  const handleSelectEvaulation = useCallback(
    async (e: ChangeEvent<HTMLInputElement>, assessmentId: number) => {
      try {
        setLoadingAssessmentIds((prevLoadingAssessmentIds) => [
          ...prevLoadingAssessmentIds,
          assessmentId
        ]);
        if (e.target.checked) {
          const { data: assessment } = await getAssessment(assessmentId);

          if (assessment) {
            setSelectedAssessments((prevSelectedAssessments) => [
              ...prevSelectedAssessments,
              assessment
            ]);
            setSelectedAssessment(assessment);
          }
        } else {
          setSelectedAssessments((prevSelectedAssessments) => [
            ...prevSelectedAssessments.filter(
              (selectedAssessment) => selectedAssessment.id !== assessmentId
            )
          ]);
        }
        setLoadingAssessmentIds((prevLoadingAssessmentIds) =>
          prevLoadingAssessmentIds.filter(
            (loadingAssessmentId) => loadingAssessmentId !== assessmentId
          )
        );
      } catch (e) {
        toast.error('anErrorOccurred');
      }
    },
    [setSelectedAssessment, setSelectedAssessments]
  );

  useEffect(() => {
    handleLoadAssessments();
  }, [handleLoadAssessments]);

  const switchableItems = [
    'type',
    'number',
    'assessmentDate',
    'deadLine',
    'lastUpdate',
    'assessor',
    'result',
    'status',
    'group',
    'name',
    'version'
  ];

  const header: (string | JSX.Element | null)[] = [
    '',
    t('assessmentsView.tableHeaders.type'),
    t('assessmentsView.tableHeaders.number'),
    t('assessmentsView.tableHeaders.assessmentDate'),
    t('assessmentsView.tableHeaders.deadLine'),
    t('assessmentsView.tableHeaders.lastUpdate'),
    t('assessmentsView.tableHeaders.assessmentLeader'),
    <FontAwesomeIcon key="bullseye" icon={faBullseye} />,
    <FontAwesomeIcon key="squarePen" icon={faSquarePen} />,
    <FontAwesomeIcon key="peopleGroup" icon={faPeopleGroup} />,
    t('assessmentsView.tableHeaders.name'),
    t('assessmentsView.tableHeaders.version'),
    <Flex key="options" ml="auto">
      <ColumnsOptions
        switchableItems={switchableItems}
        hiddenItems={hiddenItems}
        setHiddenItems={setHiddenItems}
        labelPrefix="assessmentsView.tableHeaders."
      />
    </Flex>
  ];

  const parseData = (data: IAssessment[]) => {
    const methodologies = parseItemsToMethodologyTree(data);

    // preparing date to display in table
    const tableData = methodologies.map((methodology) => ({
      colType: 'internallyAccordable',
      primaryAccordion: <Flex>{methodology.name}</Flex>,
      children: methodology.practices.map((practice) => ({
        secondaryAccordion: {
          value: practice.name,
          bgColor: theme.palette.accent.eucalyptus
        },
        children: practice.items.map((assessment) => ({
          checkbox: loadingAssessmentIds.includes(assessment.id) ? (
            <Loader />
          ) : (
            <Checkbox
              unCheckedIconDisplay
              variant="secondary"
              checked={
                !!selectedAssessments.find(({ id }) => assessment.id === id)
              }
              onChange={(e) => {
                handleSelectEvaulation(e, assessment.id);
              }}
            />
          ),
          type: (
            <FontAwesomeIcon
              icon={
                assessment.assessment_type === 'normal'
                  ? faRegluarFileLines
                  : faFileLines
              }
              size="2xl"
              color={typeColorsDict[assessment.assessment_type]}
            />
          ),
          number: assessment.number,
          assessmentDate: assessment.date,
          deadLine: assessment.realization_date,
          lastUpdate:
            assessment.updated_at &&
            format(new Date(assessment.updated_at), dateFormat),
          assessor: (
            <>
              <Flex justifyContent="flex-start" alignItems="center">
                <Avatar size="extra-small" />
                <Box ml={2}>{assessment.leader?.username}</Box>
              </Flex>
            </>
          ),
          result: assessment.avg_score,
          status: <FontAwesomeIcon icon={faLockOpen} />,
          group:
            assessment.group === 'operators' ? (
              <FontAwesomeIcon
                icon={faPeopleLine}
                color={theme.palette.accent.red}
              />
            ) : assessment.group === 'management' ? (
              <FontAwesomeIcon
                icon={faPeopleRoof}
                color={theme.palette.accent.blue}
              />
            ) : (
              ''
            ),
          name: assessment.name,
          version: assessment.version,
          options: <OptionButtons assessment={assessment} />
        }))
      }))
    }));

    return tableData;
  };

  const filterSelectedAssessmentByNumber = useCallback(
    (number: string) => {
      return selectedAssessments?.find(
        (assessment) => assessment.number === number
      );
    },
    [selectedAssessments]
  );

  return (
    <Flex flexDirection="column">
      {showAllButton && (
        <Flex>
          <Button
            onClick={() => {
              navigate('/app/assessments');
            }}
          >
            {t('common.showAll')}
          </Button>
        </Flex>
      )}
      <Table
        header={header}
        data={parseData(assessments)}
        leftFixedKeys={[0, 1]}
        hiddenItems={hiddenItems}
        onClickRow={(assessment) => {
          setSelectedAssessment(
            filterSelectedAssessmentByNumber(assessment.number)
          );
        }}
      />
      <EditAssessmentModal
        isOpen={!!assessmentToEditId}
        onCancelClick={() => setAssessmentToEditId(undefined)}
      />
    </Flex>
  );
};
